import { convertBlockType, createEmptyBlock } from "./noteBlocks";

export function blockEditableSource(block, field = "text") {
  if (!block) return "";
  if (block.type === "toggle") {
    return field === "body" ? block.body || "" : block.title || "";
  }
  if (block.type === "divider") return "";
  return block.text || "";
}

export function blockEditableLines(block, field = "text") {
  const source = blockEditableSource(block, field);
  if (!source) return [""];
  return source.split("\n");
}

export function blockHasMultipleLines(block, field = "text") {
  return blockEditableSource(block, field).includes("\n");
}

/** Map a markdown character range to inclusive line indices. */
export function getSelectedLineRangeInText(text, selStart, selEnd) {
  const source = text || "";
  const lines = source.split("\n");
  if (lines.length <= 1) {
    return { startLine: 0, endLine: 0 };
  }

  const start = Math.max(0, Math.min(selStart ?? 0, source.length));
  const end = Math.max(0, Math.min(selEnd ?? start, source.length));
  const lo = Math.min(start, end);
  const hi = Math.max(start, end);

  let startLine = 0;
  let endLine = lines.length - 1;
  let offset = 0;

  for (let i = 0; i < lines.length; i += 1) {
    const lineStart = offset;
    const lineEnd = offset + lines[i].length;

    if (lo >= lineStart && lo <= lineEnd) startLine = i;
    if (hi >= lineStart && hi <= lineEnd) endLine = i;

    offset = lineEnd + 1;
  }

  return { startLine, endLine: Math.max(startLine, endLine) };
}

function lineAsParagraph(line) {
  return { type: "paragraph", text: line };
}

function preserveLineBlock(block, line) {
  if (!block || block.type === "divider" || block.type === "toggle") {
    return lineAsParagraph(line);
  }
  if (block.type === "paragraph") {
    return lineAsParagraph(line);
  }
  return { ...block, text: line };
}

/**
 * Split a block on newlines and apply formatting only to lines in [startLine, endLine].
 * Unselected lines keep their prior block type (one block per line).
 */
export function formatSelectedLinesInBlock(block, formatType, startLine, endLine, field = "text") {
  if (!block || block.type === "divider") return [block];

  const lines = blockEditableLines(block, field);
  if (lines.length <= 1 && !blockHasMultipleLines(block, field)) {
    return [convertBlockType(block, formatType)];
  }

  const sl = Math.max(0, Math.min(startLine, lines.length - 1));
  const el = Math.max(sl, Math.min(endLine, lines.length - 1));

  return lines.map((line, index) => {
    if (index >= sl && index <= el) {
      return convertBlockType(lineAsParagraph(line), formatType);
    }
    return preserveLineBlock(block, line);
  });
}

export function formatEntireBlockAsLines(block, formatType, field = "text") {
  const lines = blockEditableLines(block, field);
  if (lines.length <= 1 && !blockHasMultipleLines(block, field)) {
    return [convertBlockType(block, formatType)];
  }
  return formatSelectedLinesInBlock(block, formatType, 0, lines.length - 1, field);
}

export function getActiveEditorContext(root) {
  const active = document.activeElement;
  if (!active?.isContentEditable || !root?.contains(active)) return null;

  const blockEl = active.closest("[data-note-block-index]");
  if (!blockEl) return null;

  const blockIndex = Number(blockEl.dataset.noteBlockIndex);
  if (Number.isNaN(blockIndex)) return null;

  return {
    blockIndex,
    field: active.dataset.noteBlockField || "text",
    el: active,
  };
}

export function replaceBlocksSlice(blocks, index, replacement) {
  if (!replacement.length) return [{ type: "paragraph", text: "" }];
  const next = [...blocks];
  next.splice(index, 1, ...replacement);
  return next.length ? next : [createEmptyBlock("paragraph")];
}
